/**
 * UI adapter: bridges React grid actions to PlaceBuildingUseCase (UC-02)
 * Enable via architectureFeatures.usePlaceBuildingUseCase
 */
import { useCallback } from 'react';
import { BuildingType } from '../../../../types';
import type { Grid } from '../../../../types';
import { BUILDINGS } from '../../../../constants';
import { placeBuildingUseCase } from '../../../infrastructure/di/container';
import { architectureFeatures } from '../../../infrastructure/config/features';
import { simulationEngine } from '../../../domain/services/SimulationEngine';
import type {
  PlaceBuildingPlaceInput,
  UpgradeBuildingInput,
  DeleteBuildingInput,
  PlaceBuildingPreviewInput,
  PlaceBuildingPreviewOutput,
  PlaceBuildingOutput,
} from '../../../application/dto/PlaceBuildingDto';

export type {
  PlaceBuildingPlaceInput,
  UpgradeBuildingInput,
  DeleteBuildingInput,
  PlaceBuildingPreviewInput,
  PlaceBuildingPreviewOutput,
  PlaceBuildingOutput,
};

function cloneGrid(grid: Grid): Grid {
  return grid.map((row) => row.map((tile) => ({ ...tile })));
}

function computeUpgradeCost(buildingType: BuildingType, level: number): number {
  const config = BUILDINGS[buildingType];
  return Math.floor(config.cost * level * 1.2);
}

/** Run place through clean architecture use case (UC-02) */
export function executePlaceBuilding(input: PlaceBuildingPlaceInput): PlaceBuildingOutput {
  if (!architectureFeatures.usePlaceBuildingUseCase) {
    throw new Error('usePlaceBuildingUseCase feature flag is disabled');
  }
  return placeBuildingUseCase.executePlace(input);
}

/** Run upgrade through clean architecture use case (UC-02) */
export function executeUpgradeBuilding(input: UpgradeBuildingInput): PlaceBuildingOutput {
  if (!architectureFeatures.usePlaceBuildingUseCase) {
    throw new Error('usePlaceBuildingUseCase feature flag is disabled');
  }
  return placeBuildingUseCase.executeUpgrade(input);
}

/** Run delete through clean architecture use case (UC-02) */
export function executeDeleteBuilding(input: DeleteBuildingInput): PlaceBuildingOutput {
  if (!architectureFeatures.usePlaceBuildingUseCase) {
    throw new Error('usePlaceBuildingUseCase feature flag is disabled');
  }
  return placeBuildingUseCase.executeDelete(input);
}

/** Preview place/upgrade/delete without mutating grid (UC-02) */
export function computeBuildingPreview(
  input: PlaceBuildingPreviewInput,
): PlaceBuildingPreviewOutput {
  if (!architectureFeatures.usePlaceBuildingUseCase) {
    throw new Error('usePlaceBuildingUseCase feature flag is disabled');
  }
  return placeBuildingUseCase.computePreview(input);
}

/** Legacy fallback — inline place logic (migration helper) */
export function executeLegacyPlaceBuilding(input: PlaceBuildingPlaceInput): PlaceBuildingOutput {
  const { grid, stats, x, y, buildingType } = input;
  const currentTile = grid[y][x];
  const buildingConfig = BUILDINGS[buildingType];

  if (currentTile.buildingType !== BuildingType.None) {
    return {
      ok: false,
      grid,
      stats,
      budgetDelta: 0,
      connectivityRefreshed: false,
      error: 'Tile is already occupied.',
    };
  }

  if (stats.budget < buildingConfig.cost) {
    return {
      ok: false,
      grid,
      stats,
      budgetDelta: 0,
      connectivityRefreshed: false,
      error: 'Insufficient funds.',
    };
  }

  const newGrid = cloneGrid(grid);
  newGrid[y][x] = { ...currentTile, buildingType, level: 1, isConnected: false };

  return {
    ok: true,
    grid: newGrid,
    stats: { ...stats, budget: stats.budget - buildingConfig.cost },
    budgetDelta: -buildingConfig.cost,
    connectivityRefreshed: false,
  };
}

/** Legacy fallback — inline upgrade logic (migration helper) */
export function executeLegacyUpgradeBuilding(input: UpgradeBuildingInput): PlaceBuildingOutput {
  const { grid, stats, x, y, hasJumpGate } = input;
  const tile = grid[y][x];

  if (!hasJumpGate) {
    return {
      ok: false,
      grid,
      stats,
      budgetDelta: 0,
      connectivityRefreshed: false,
      error: 'ACCESS DENIED: Upgrades require Jump Gate.',
    };
  }

  const level = tile.level || 1;
  const cost = computeUpgradeCost(tile.buildingType, level);

  if (level >= 5) {
    return {
      ok: false,
      grid,
      stats,
      budgetDelta: 0,
      connectivityRefreshed: false,
      error: 'Building is already at maximum level.',
    };
  }

  if (stats.budget < cost) {
    return {
      ok: false,
      grid,
      stats,
      budgetDelta: 0,
      connectivityRefreshed: false,
      error: 'Insufficient funds for upgrade.',
    };
  }

  const newGrid = cloneGrid(grid);
  newGrid[y][x] = { ...newGrid[y][x], level: level + 1 };

  return {
    ok: true,
    grid: newGrid,
    stats: { ...stats, budget: stats.budget - cost },
    budgetDelta: -cost,
    connectivityRefreshed: false,
  };
}

/** Legacy fallback — inline delete logic (migration helper) */
export function executeLegacyDeleteBuilding(input: DeleteBuildingInput): PlaceBuildingOutput {
  const { grid, stats, x, y } = input;
  const tile = grid[y][x];

  if (tile.buildingType === BuildingType.Core) {
    return {
      ok: false,
      grid,
      stats,
      budgetDelta: 0,
      connectivityRefreshed: false,
      error: 'Cannot dismantle The HUB.',
    };
  }

  const newGrid = cloneGrid(grid);
  newGrid[y][x] = { ...newGrid[y][x], buildingType: BuildingType.None, level: 1 };

  return {
    ok: true,
    grid: newGrid,
    stats,
    budgetDelta: 0,
    connectivityRefreshed: false,
  };
}

/** Resolve place — UC-02 when flag on, else legacy path */
export function resolvePlaceBuilding(input: PlaceBuildingPlaceInput): PlaceBuildingOutput {
  if (architectureFeatures.usePlaceBuildingUseCase) {
    return executePlaceBuilding(input);
  }
  return executeLegacyPlaceBuilding(input);
}

/** Resolve upgrade — UC-02 when flag on, else legacy path */
export function resolveUpgradeBuilding(input: UpgradeBuildingInput): PlaceBuildingOutput {
  if (architectureFeatures.usePlaceBuildingUseCase) {
    return executeUpgradeBuilding(input);
  }
  return executeLegacyUpgradeBuilding(input);
}

/** Resolve delete — UC-02 when flag on, else legacy path */
export function resolveDeleteBuilding(input: DeleteBuildingInput): PlaceBuildingOutput {
  if (architectureFeatures.usePlaceBuildingUseCase) {
    return executeDeleteBuilding(input);
  }
  return executeLegacyDeleteBuilding(input);
}

/** Apply connectivity refresh — use case path returns connected grid; legacy defers to gameStore */
export function refreshGridConnectivityAfterMutation(grid: Grid): Grid {
  if (architectureFeatures.usePlaceBuildingUseCase) {
    return grid;
  }
  return simulationEngine.calculateConnectivity(grid);
}

/**
 * Hook helper for useGridActions integration.
 * Returns stable callbacks that run UC-02 when feature flag is on.
 */
export function usePlaceBuildingRunner() {
  const runPlace = useCallback(
    (input: PlaceBuildingPlaceInput): PlaceBuildingOutput | null => {
      if (!architectureFeatures.usePlaceBuildingUseCase) {
        return null;
      }
      return executePlaceBuilding(input);
    },
    [],
  );

  const runUpgrade = useCallback(
    (input: UpgradeBuildingInput): PlaceBuildingOutput | null => {
      if (!architectureFeatures.usePlaceBuildingUseCase) {
        return null;
      }
      return executeUpgradeBuilding(input);
    },
    [],
  );

  const runDelete = useCallback(
    (input: DeleteBuildingInput): PlaceBuildingOutput | null => {
      if (!architectureFeatures.usePlaceBuildingUseCase) {
        return null;
      }
      return executeDeleteBuilding(input);
    },
    [],
  );

  const runPreview = useCallback(
    (input: PlaceBuildingPreviewInput): PlaceBuildingPreviewOutput | null => {
      if (!architectureFeatures.usePlaceBuildingUseCase) {
        return null;
      }
      return computeBuildingPreview(input);
    },
    [],
  );

  return { runPlace, runUpgrade, runDelete, runPreview };
}